import React from 'react';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { useHistory } from 'react-router';

import { Stack } from '@mui/material';

import { createFlightCampaign, deleteFlightCampaign } from '~/services/FlightsService';

import { FLIGHT_CAMPAIGNS_QUERY_KEY, useFlightCampaigns } from '../hooks/useFlightCampaigns';

import { FlightsCampaignsForm, FormState } from './FlightsCampaignsForm';
import { FlightsCampaignsTable } from './FlightsCampaignsTable';

export function FlightsCampaings() {
  const { enqueueSnackbar } = useSnackbar();
  const history = useHistory();
  const queryClient = useQueryClient();
  const { error: fetchingDataError, loading: loadingData, data } = useFlightCampaigns();

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteFlightCampaign(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [FLIGHT_CAMPAIGNS_QUERY_KEY] });
      enqueueSnackbar('Campaign was deleted', { variant: 'success' });
    },
    onError: () => {
      enqueueSnackbar('Something went wrong, please try again', { variant: 'error' });
    },
  });

  const createMutation = useMutation({
    mutationFn: (formData: FormState) =>
      createFlightCampaign({
        brand: formData.brand.join(','),
        type: formData.type,
        title: formData.title,
        region: formData.region,
        header: formData.header,
        description: formData.description,
        start_date: formData.startDate,
        end_date: formData.endDate,
        hero_image_public_id: formData.heroImage,
        terms: formData.terms,
        airline_code: formData.airlineCode,
        price: parseFloat(formData.price),
        fare_class: formData.fareClass,
        fare_type: formData.fareType,
      }),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: [FLIGHT_CAMPAIGNS_QUERY_KEY] });
      enqueueSnackbar('Campaign was created', { variant: 'success' });
      history.push(`/flights/campaigns/${response.result.id}`);
    },
    onError: () => {
      enqueueSnackbar('Something went wrong, please try again', { variant: 'error' });
    },
  });

  const handleDelete = (id: string) => {
    deleteMutation.mutate(id);
  };

  const handleSubmit = (formData: FormState) => {
    createMutation.mutate(formData);
  };

  return (
    <Stack gap={6}>
      <FlightsCampaignsForm
        onSubmit={handleSubmit}
        error={createMutation.isError ? createMutation.error?.message : undefined}
        loading={createMutation.isPending}
      />
      <FlightsCampaignsTable
        data={data}
        onDelete={handleDelete}
        error={fetchingDataError?.message}
        loading={loadingData}
      />
    </Stack>
  );
}
